import type { OrderStatus, OrderStatusEntry } from '../types';

export const orderTrackingFlow = ['Processed', 'Shipped', 'En Route', 'Arrived'] as const satisfies readonly OrderStatus[];
export const digitalOrderTrackingFlow = ['Pending', 'Paid'] as const satisfies readonly OrderStatus[];

export const isDigitalOrderStatus = (status: string): boolean =>
  status === 'Pending' || status === 'Paid' || status === 'Failed';

export const isDeliveredOrderStatus = (status: string): boolean =>
  status === 'Arrived' || status === 'Delivered' || status === 'Paid';

export const isClosedOrderTrackingStatus = (status: string): boolean =>
  isDeliveredOrderStatus(status) || status === 'Cancelled' || status === 'Failed';

export const getOrderTrackingIndex = (
  status: OrderStatus,
  history: OrderStatusEntry[] = [],
): number => {
  if (isDigitalOrderStatus(status)) {
    const directDigitalIndex = digitalOrderTrackingFlow.indexOf(status as (typeof digitalOrderTrackingFlow)[number]);
    if (directDigitalIndex >= 0) {
      return directDigitalIndex;
    }

    const lastReachedDigitalStep = history
      .map((entry) => digitalOrderTrackingFlow.indexOf(entry.status as (typeof digitalOrderTrackingFlow)[number]))
      .filter((index): index is number => index >= 0)
      .sort((left, right) => right - left)[0];

    return lastReachedDigitalStep ?? 0;
  }

  const directIndex = orderTrackingFlow.indexOf(status as (typeof orderTrackingFlow)[number]);
  if (directIndex >= 0) {
    return directIndex;
  }

  const lastReachedStep = history
    .map((entry) => orderTrackingFlow.indexOf(entry.status as (typeof orderTrackingFlow)[number]))
    .filter((index): index is number => index >= 0)
    .sort((left, right) => right - left)[0];

  return lastReachedStep ?? 0;
};

export const getOrderTrackingProgressPercent = (
  status: OrderStatus,
  history: OrderStatusEntry[] = [],
): number => {
  if (orderTrackingFlow.length <= 1) {
    return 0;
  }

  return (getOrderTrackingIndex(status, history) / (orderTrackingFlow.length - 1)) * 100;
};
